import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { CoverageBar } from '@/components/charts/Bars'
import { AccountRow, List } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList } from '@/components/states'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { coverageSegments } from '@/lib/coverage'
import { formatDate } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/** Every account built from imported statements, with balance and coverage. */
export function AccountsScreen() {
  const navigate = useNavigate()
  const accounts = useAsync(() => api.accounts.list(), [])

  const rows = accounts.state.data ?? []

  const screenState =
    accounts.state.status === 'error'
      ? 'error'
      : accounts.state.status === 'loading' && !accounts.state.data
        ? 'loading'
        : rows.length === 0
          ? 'empty'
          : 'ready'

  return (
    <Screen
      id="accounts"
      state={screenState}
      actions={
        screenState === 'ready' ? (
          <>
            <Button variant="secondary" onClick={() => navigate('connectedAccounts')}>
              {t('settings.row.connectedAccounts')}
            </Button>
            <Button variant="primary" onClick={() => navigate('upload')}>
              {t('accounts.cta.add')}
            </Button>
          </>
        ) : undefined
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonList rows={4} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="accounts.error.title"
          error={accounts.state.status === 'error' ? accounts.state.error : undefined}
          onRetry={accounts.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState
          titleKey="accounts.empty.title"
          actionLabel={t('accounts.empty.cta')}
          onAction={() => navigate('upload')}
          secondaryLabel={t('accounts.cta.link')}
          onSecondary={() => navigate('linkAccount')}
        />
      ) : (
        <div className="stack">
          <List>
            {rows.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                onSelect={() => navigate('accountDetail', { id: account.id })}
              />
            ))}
          </List>

          <div className="stack-sm">
            {rows
              .filter((account) => account.coverage?.from && account.coverage.to)
              .map((account) => (
                <div className="card stack-sm" key={account.id}>
                  <span className="label">{account.name}</span>
                  <CoverageBar
                    segments={coverageSegments(account.coverage)}
                    label={t('accounts.row.coverage', {
                      from: formatDate(account.coverage!.from),
                      to: formatDate(account.coverage!.to),
                    })}
                  />
                  <span className="caption">
                    {t('accounts.row.coverage', {
                      from: formatDate(account.coverage!.from),
                      to: formatDate(account.coverage!.to),
                    })}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </Screen>
  )
}
